import { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

// Приблизительная высота поповера для выбора направления открытия (4 строки + отступы).
const POPOVER_HEIGHT = 180;

function buildMinutes(step) {
  const safeStep = Number.isFinite(step) && step > 0 ? step : 15;
  const list = [];
  for (let m = 0; m < 60; m += safeStep) {
    list.push(String(m).padStart(2, '0'));
  }
  return list;
}

function parseValue(value) {
  if (typeof value === 'string' && /^\d{1,2}:\d{2}/.test(value)) {
    const [h, m] = value.slice(0, 5).split(':');
    return { hour: h.padStart(2, '0'), minute: m };
  }
  return { hour: '', minute: '' };
}

const ClockIcon = () => (
  <svg
    className="ui-timepicker__icon"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

export const UiTimePicker = forwardRef(function UiTimePicker(
  {
    label,
    error,
    className = '',
    style,
    inputClassName = '',
    value = '',
    onChange,
    disabled = false,
    required = false,
    placeholder = 'чч:мм',
    minuteStep = 15,
    name,
    id,
    ...rest
  },
  ref,
) {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState({ left: 0, top: 0, bottom: undefined });
  const rootRef = useRef(null);
  const popRef = useRef(null);
  const hoursColRef = useRef(null);
  const minutesColRef = useRef(null);

  const minutes = useMemo(() => buildMinutes(minuteStep), [minuteStep]);
  const { hour, minute } = parseValue(value);
  const display = hour && minute ? `${hour}:${minute}` : '';

  const emit = (nextHour, nextMinute) => {
    const next = `${nextHour}:${nextMinute}`;
    onChange?.({ target: { value: next, name } });
  };

  const selectHour = (h) => emit(h, minute || minutes[0]);
  const selectMinute = (m) => {
    emit(hour || HOURS[0], m);
    setOpen(false);
  };

  const updatePosition = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const pad = 4;
    const below = window.innerHeight - r.bottom - pad;
    const above = r.top - pad;
    // Открываем вверх, если снизу мало места, а сверху больше.
    const flip = below < POPOVER_HEIGHT && above > below;
    setBox({
      left: r.left,
      top: flip ? undefined : r.bottom + pad,
      bottom: flip ? window.innerHeight - r.top + pad : undefined,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener('resize', onResize);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (rootRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    for (const col of [hoursColRef.current, minutesColRef.current]) {
      const selected = col?.querySelector('[data-selected="true"]');
      if (selected) selected.scrollIntoView({ block: 'center' });
    }
  }, [open]);

  const popStyle = {
    position: 'fixed',
    left: box.left,
    zIndex: 10050,
    ...(box.top != null ? { top: box.top } : { bottom: box.bottom }),
  };

  return (
    <div className={`ui-field ${className}`.trim()} style={style}>
      {label ? <span className="ui-field__label">{label}</span> : null}
      <div className="ui-timepicker" ref={rootRef}>
        <button
          type="button"
          ref={ref}
          id={id}
          name={name}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-required={required}
          className={`ui-control ui-timepicker__control ${inputClassName}`.trim()}
          onClick={() => !disabled && setOpen((prev) => !prev)}
          {...rest}
        >
          <span className={display ? 'ui-timepicker__value' : 'ui-timepicker__placeholder'}>
            {display || placeholder}
          </span>
          <ClockIcon />
        </button>

        {open && !disabled
          ? createPortal(
              <div className="ui-timepicker__pop" role="dialog" ref={popRef} style={popStyle}>
                <div className="ui-timepicker__col" ref={hoursColRef}>
                  {HOURS.map((h) => (
                    <button
                      key={h}
                      type="button"
                      data-selected={h === hour}
                      className={`ui-timepicker__option ${h === hour ? 'is-selected' : ''}`.trim()}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectHour(h)}
                    >
                      {h}
                    </button>
                  ))}
                </div>
                <div className="ui-timepicker__col" ref={minutesColRef}>
                  {minutes.map((m) => (
                    <button
                      key={m}
                      type="button"
                      data-selected={m === minute}
                      className={`ui-timepicker__option ${m === minute ? 'is-selected' : ''}`.trim()}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectMinute(m)}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>,
              document.body,
            )
          : null}
      </div>
      {error ? <span className="form-error">{error}</span> : null}
    </div>
  );
});
